import { httpsCallable } from "firebase/functions";
import { toLower, toUpper } from 'lodash';
import moment from 'moment-timezone';
import { fromUtcToTimeZone } from '../utils/dateFormatter';
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberGreaterThanOrEqualToZero, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import { toE164PhoneNumber, toPhoneNumberLocal } from '../utils/stringFormatter';
import { createBlind, getAll, getById, getBySingleQuery, remove, update, updateField, updateFields } from '../utils/dbUtils';
import AuthUser from './AuthUser';
import { collection, doc, documentId, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where, writeBatch } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { asTimeZone } from '../utils/dateFormatter';
import { getClientIPAddress, getClientUserAgent, getReferrerUrl } from '../utils/clientUtils';
import Subscription from './Subscription';
import User from './User';
import UserInsurance from './UserInsurance';
import UserNote from './UserNote';
import { addLeadFile, getFileExt } from '../firebase/StorageService';
import { v4 as uuid } from 'uuid';
import StatusHistory from './StatusHistory';
import Address from './Address';
import PromisePool from "@supercharge/promise-pool";
import CartItem from './CartItem';
import Product from './Product';
import ProductVariant from './ProductVariant';
import { getProductSkuById, getVariantNameById, getVariantSkuById } from '../utils/entityUtils';
import AttributionHistoryItem from './AttributionHistoryItem';

class Lead {
	static collectionName = 'leads';

	userId = null;
	userRefId = null;

	status = "PENDING";
	statusHistory = []; // list of StatusHistory, historical changes to status

	email = null;
	phone = null;

	firstName = null;
	firstNameLower = null;
	middleName = null;
	lastName = null;
	lastNameLower = null;
	suffix = null;

	gender = null;
	dateOfBirth = null;

	height = 0;
	weight = 0;
	bmi = 0.0;

	socialSecurityNumber = null;
	socialSecurityNumberLastFour = null;

	shippingAddress = new Address(null).toDto();
	billingAddress = new Address(null).toDto();

	subscriberId = null;
	payerName = null;
	payerCode = null;
	planType = null;
	planName = null;
	insuranceStatus = 'UNKNOWN';
	rxBin = null;
	rxPcn = null;

	conversionId = null;
	isConversion = false;

	source = null;
	affiliateId = null;
	subAffiliateId = null;
	campaignId = null;
	programId = null;
	productSku = null;
	productFamilyId = null;
	offerGroupId = null;
	offerToken = null;
	commercialEquivalencyGroupId = null;

	affiliateInfo = null;
	attributionHistory = []; // AttributionHistoryItem[] - append-only meaningful attribution touches for future multi-touch attribution. Current attribution still lives on affiliateInfo/affiliateId/subAffiliateId.
	deviceInfo = null;
	emailInfo = null;
	phoneInfo = null;

	insuranceInfo = null;
	hasMedicare = false;
	hasMedicaid = false;

	errorMessage = null;

	ipAddress = null;
	userAgent = null;
	referrerUrl = null;

	importedAt = null; // converted date for non-insurance (billed by us) products

	insuranceCall = null;
	originalSubmission = null;
	originalLookupSubmission = null;

	sentWelcomeEmail = false;
	sentEmails = [];

	notes = [];
	notesCount = 0;
	documents = [];
	documentsCount = 0;
	agreements = [];
	agreementsCount = 0;
	forms = [];
	formsCount = 0;

	coveredEntityId = null;
	pharmacyId = null;

	phoneVerified = false;

	testingReason = [];

	acceptAgreements = false;
	certifySymptoms = false;
	acceptConsentAgreements = false;

	orderIds = [];
	subscriptionIds = [];

	driversLicenseFrontImageRefId = null;
	#driversLicenseFrontImage = null;
	driversLicenseBackImageRefId = null;
	#driversLicenseBackImage = null;

	selfImageRefId = null;
	#selfImage = null;

	requiresConfirmation = false; // requires patient confirmation - needs to be set to true if there are surveys and they need to be filled out for each order
	isConfirmed = false;
	confirmedAt = null; // date the order is confirmed

	requiresClinicianApproval = false; // requires that the doctor prescribes/approves the product
	isApproved = false;
	approvedAt = null; // date the order is approved by the clinician

	requiresClinicianEncounter = false; // requires an in-person or telehealth dr appt
	encounterIds = []; // reference to the associated encounter/visit setup for this order
	encounterAt = null;

	requiresSurveys = false; // requires patient to fill out forms/surveys
	isSurveysCompleted = false;
	surveysCompletedAt = null;

	// require documents to be provided when ordering product
	requiredDocuments = []; // array of object { documentType, requirement, scope } requirement: enum DocumentRequirements, scope: enum SurveyScopes
	isRequiredDocumentsProvided = false;
	requiredDocumentsProvidedAt = null;

	lastFunnelStep = 0;
	cartItems = [];

	shippingState = null;

	repId = 'NONE'; // userId of customer representative

	couponCode = null;
	referralUserId = null;
	referralAffiliateId = null;
	referralSubAffiliateId = null;

	testMode = false;

	addon = false;

	constructor(lead) {
		if (isObjectNotEmpty(lead)) {
			if (isStringNotEmpty(lead.userId)) { this.userId = lead.userId; }
			if (isStringNotEmpty(lead.userRefId)) { this.userRefId = lead.userRefId; }

			if (isStringNotEmpty(lead.status)) { this.status = lead.status; }
			if (isListNotEmpty(lead.statusHistory)) { this.statusHistory = lead.statusHistory; }
			else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status); }

			if (isStringNotEmpty(lead.email)) { this.email = toLower(lead.email.trim()); }
			if (isStringNotEmpty(lead.phone)) { this.phone = toE164PhoneNumber(lead.phone); }

			if (isStringNotEmpty(lead.firstName)) {
				this.firstName = lead.firstName;
				this.firstNameLower = toLower(lead.firstName);
			}
			if (isStringNotEmpty(lead.lastName)) {
				this.lastName = lead.lastName;
				this.lastNameLower = toLower(lead.lastName);
			}
			if (isStringNotEmpty(lead.middleName)) { this.middleName = lead.middleName.trim(); }
			if (isStringNotEmpty(lead.suffix)) { this.suffix = lead.suffix; }

			if (isStringNotEmpty(lead.gender)) { this.gender = lead.gender; }
			if (isStringNotEmpty(lead.dateOfBirth)) { this.dateOfBirth = lead.dateOfBirth; }


			if (isNumberGreaterThanOrEqualToZero(lead.height)) { this.height = lead.height; }
			if (isNumberGreaterThanOrEqualToZero(lead.weight)) { this.weight = lead.weight; }
			if (isNumberGreaterThanOrEqualToZero(lead.bmi)) { this.bmi = lead.bmi; }

			if (isStringNotEmpty(lead.socialSecurityNumber)) { this.socialSecurityNumber = lead.socialSecurityNumber; }
			if (isStringNotEmpty(lead.socialSecurityNumberLastFour)) { this.socialSecurityNumberLastFour = lead.socialSecurityNumberLastFour; }

			if (isObjectNotEmpty(lead.shippingAddress)) { this.shippingAddress = new Address(lead.shippingAddress).toDto(); }
			if (isObjectNotEmpty(lead.billingAddress)) { this.billingAddress = new Address(lead.billingAddress).toDto(); }

			if (isStringNotEmpty(lead.subscriberId)) { this.subscriberId = lead.subscriberId; }
			if (isStringNotEmpty(lead.payerName)) { this.payerName = lead.payerName; }
			if (isStringNotEmpty(lead.payerCode)) { this.payerCode = lead.payerCode; }
			if (isStringNotEmpty(lead.planType)) { this.planType = lead.planType; }
			if (isStringNotEmpty(lead.planName)) { this.planName = lead.planName; }
			if (isStringNotEmpty(lead.insuranceStatus)) { this.insuranceStatus = lead.insuranceStatus; }
			if (isStringNotEmpty(lead.rxBin)) { this.rxBin = lead.rxBin; }
			if (isStringNotEmpty(lead.rxPcn)) { this.rxPcn = lead.rxPcn; }

			if (isStringNotEmpty(lead.conversionId)) { this.conversionId = lead.conversionId; }
			if (isBooleanTrue(lead.isConversion)) { this.isConversion = true; }

			if (isStringNotEmpty(lead.source)) { this.source = lead.source; }
			if (isStringNotEmpty(lead.affiliateId)) { this.affiliateId = toLower(lead.affiliateId); }
			if (isStringNotEmpty(lead.subAffiliateId)) { this.subAffiliateId = lead.subAffiliateId; }
			if (isStringNotEmpty(lead.campaignId)) { this.campaignId = lead.campaignId; }
			if (isStringNotEmpty(lead.programId)) { this.programId = lead.programId; }
			if (isStringNotEmpty(lead.productSku)) { this.productSku = lead.productSku; }
			if (isStringNotEmpty(lead.productFamilyId)) { this.productFamilyId = lead.productFamilyId; }
			if (isStringNotEmpty(lead.offerGroupId)) { this.offerGroupId = lead.offerGroupId; }
			if (isStringNotEmpty(lead.offerToken)) { this.offerToken = lead.offerToken; }
			if (isStringNotEmpty(lead.commercialEquivalencyGroupId)) { this.commercialEquivalencyGroupId = lead.commercialEquivalencyGroupId; }

			if (isObjectNotEmpty(lead.affiliateInfo)) { this.affiliateInfo = lead.affiliateInfo; }
			if (isListNotEmpty(lead.attributionHistory)) { this.attributionHistory = lead.attributionHistory.map((item) => new AttributionHistoryItem(item).toDto()); }
			if (isObjectNotEmpty(lead.deviceInfo)) { this.deviceInfo = lead.deviceInfo; }
			if (isObjectNotEmpty(lead.emailInfo)) { this.emailInfo = lead.emailInfo; }
			if (isObjectNotEmpty(lead.phoneInfo)) { this.phoneInfo = lead.phoneInfo; }

			if (isObjectNotEmpty(lead.insuranceInfo)) { this.insuranceInfo = lead.insuranceInfo; }
			if (isBooleanTrue(lead.hasMedicare)) { this.hasMedicare = true; }
			if (isBooleanTrue(lead.hasMedicaid)) { this.hasMedicaid = true; }

			if (isStringNotEmpty(lead.errorMessage)) { this.errorMessage = lead.errorMessage; }

			if (isStringNotEmpty(lead.ipAddress)) { this.ipAddress = lead.ipAddress; }
			if (isStringNotEmpty(lead.userAgent)) { this.userAgent = lead.userAgent; }
			if (isStringNotEmpty(lead.referrerUrl)) { this.referrerUrl = lead.referrerUrl; }

			if (isNotNullOrUndefined(lead.importedAt)) { this.importedAt = lead.importedAt; }

			if (isStringNotEmpty(lead.insuranceCall)) { this.insuranceCall = lead.insuranceCall; }
			if (isObjectNotEmpty(lead.originalSubmission)) { this.originalSubmission = lead.originalSubmission; }
			if (isObjectNotEmpty(lead.originalLookupSubmission)) { this.originalLookupSubmission = lead.originalLookupSubmission; }

			if (isBooleanTrue(lead.sentWelcomeEmail)) { this.sentWelcomeEmail = true; }
			if (isListNotEmpty(lead.sentEmails)) { this.sentEmails = lead.sentEmails; }

			if (isListNotEmpty(lead.notes)) {
				this.notes = lead.notes;
				this.notesCount = lead.notes.length;
			}
			if (isListNotEmpty(lead.documents)) {
				this.documents = lead.documents;
				this.documentsCount = lead.documents.length;
			}
			if (isListNotEmpty(lead.agreements)) {
				this.agreements = lead.agreements;
				this.agreementsCount = lead.agreements.length;
			}
			if (isListNotEmpty(lead.forms)) {
				this.forms = lead.forms;
				this.formsCount = lead.forms.length;
			}

			if (isStringNotEmpty(lead.coveredEntityId)) { this.coveredEntityId = lead.coveredEntityId; }
			if (isStringNotEmpty(lead.pharmacyId)) { this.pharmacyId = lead.pharmacyId; }

			if (isBooleanTrue(lead.phoneVerified)) { this.phoneVerified = true; }

			if (isListNotEmpty(lead.testingReason)) { this.testingReason = lead.testingReason; }
			if (isBooleanTrue(lead.acceptAgreements)) { this.acceptAgreements = true; }
			if (isBooleanTrue(lead.certifySymptoms)) { this.certifySymptoms = true; }
			if (isBooleanTrue(lead.acceptConsentAgreements)) { this.acceptConsentAgreements = true; }

			if (isListNotEmpty(lead.orderIds)) { this.orderIds = lead.orderIds; }
			if (isListNotEmpty(lead.subscriptionIds)) { this.subscriptionIds = lead.subscriptionIds; }

			if (isStringNotEmpty(lead.driversLicenseFrontImageRefId)) { this.driversLicenseFrontImageRefId = lead.driversLicenseFrontImageRefId; }
			if (isNotNullOrUndefined(lead.driversLicenseFrontImage)) { this.#driversLicenseFrontImage = lead.driversLicenseFrontImage; }
			if (isStringNotEmpty(lead.driversLicenseBackImageRefId)) { this.driversLicenseBackImageRefId = lead.driversLicenseBackImageRefId; }
			if (isNotNullOrUndefined(lead.driversLicenseBackImage)) { this.#driversLicenseBackImage = lead.driversLicenseBackImage; }

			if (isStringNotEmpty(lead.selfImageRefId)) { this.selfImageRefId = lead.selfImageRefId; }
			if (isNotNullOrUndefined(lead.selfImage)) { this.#selfImage = lead.selfImage; }

			if (isBooleanTrue(lead.requiresConfirmation)) { this.requiresConfirmation = true; }
			if (isBooleanTrue(lead.isConfirmed)) { this.isConfirmed = true; }
			if (isNotNullOrUndefined(lead.confirmedAt)) { this.confirmedAt = lead.confirmedAt; }

			if (isBooleanTrue(lead.requiresClinicianApproval)) { this.requiresClinicianApproval = true; }
			if (isBooleanTrue(lead.isApproved)) { this.isApproved = true; }
			if (isNotNullOrUndefined(lead.approvedAt)) { this.approvedAt = lead.approvedAt; }

			if (isBooleanTrue(lead.requiresClinicianEncounter)) { this.requiresClinicianEncounter = true; }
			if (isListNotEmpty(lead.encounterIds)) { this.encounterIds = lead.encounterIds; }
			if (isNotNullOrUndefined(lead.encounterAt)) { this.encounterAt = lead.encounterAt; }

			if (isBooleanTrue(lead.requiresSurveys)) { this.requiresSurveys = true; }
			if (isBooleanTrue(lead.isSurveysCompleted)) { this.isSurveysCompleted = true; }
			if (isNotNullOrUndefined(lead.surveysCompletedAt)) { this.surveysCompletedAt = lead.surveysCompletedAt; }

			if (isListNotEmpty(lead.requiredDocuments)) { this.requiredDocuments = lead.requiredDocuments; }
			if (isBooleanTrue(lead.isRequiredDocumentsProvided)) { this.isRequiredDocumentsProvided = true; }
			if (isNotNullOrUndefined(lead.requiredDocumentsProvidedAt)) { this.requiredDocumentsProvidedAt = lead.requiredDocumentsProvidedAt; }

			if (isNumberGreaterThanOrEqualToZero(lead.lastFunnelStep)) { this.lastFunnelStep = lead.lastFunnelStep; }
			if (isListNotEmpty(lead.cartItems)) { this.cartItems = lead.cartItems; }

			if (isStringNotEmpty(lead.shippingState)) { this.shippingState = lead.shippingState; }

			if (isStringNotEmpty(lead.repId)) { this.repId = lead.repId; }

			if (isStringNotEmpty(lead.couponCode)) { this.couponCode = lead.couponCode; }
			if (isStringNotEmpty(lead.referralUserId)) { this.referralUserId = lead.referralUserId; }
			if (isStringNotEmpty(lead.referralAffiliateId)) { this.referralAffiliateId = lead.referralAffiliateId; }
			if (isStringNotEmpty(lead.referralSubAffiliateId)) { this.referralSubAffiliateId = lead.referralSubAffiliateId; }

			if (isBooleanTrue(lead.testMode)) { this.testMode = true; }

			if (isBooleanTrue(lead.addon)) { this.addon = true; }
		}
	}
	toDto = () => {
		return {
			userId: this.userId,
			userRefId: this.userRefId,

			status: this.status,
			statusHistory: this.statusHistory,

			email: this.email,
			phone: this.phone,

			firstName: this.firstName,
			firstNameLower: this.firstNameLower,
			middleName: this.middleName,
			lastName: this.lastName,
			lastNameLower: this.lastNameLower,
			suffix: this.suffix,

			gender: this.gender,
			dateOfBirth: this.dateOfBirth,

			height: this.height,
			weight: this.weight,
			bmi: this.bmi,

			socialSecurityNumber: this.socialSecurityNumber,
			socialSecurityNumberLastFour: this.socialSecurityNumberLastFour,

			shippingAddress: this.shippingAddress,
			billingAddress: this.billingAddress,

			subscriberId: this.subscriberId,
			payerName: this.payerName,
			payerCode: this.payerCode,
			planType: this.planType,
			planName: this.planName,
			insuranceStatus: this.insuranceStatus,
			rxBin: this.rxBin,
			rxPcn: this.rxPcn,

			conversionId: this.conversionId,
			isConversion: this.isConversion,

			source: this.source,
			affiliateId: this.affiliateId,
			subAffiliateId: this.subAffiliateId,
			campaignId: this.campaignId,
			programId: this.programId,
			productSku: this.productSku,
			productFamilyId: this.productFamilyId,
			offerGroupId: this.offerGroupId,
			offerToken: this.offerToken,
			commercialEquivalencyGroupId: this.commercialEquivalencyGroupId,

			affiliateInfo: this.affiliateInfo,
			attributionHistory: this.attributionHistory,
			deviceInfo: this.deviceInfo,
			emailInfo: this.emailInfo,
			phoneInfo: this.phoneInfo,

			insuranceInfo: this.insuranceInfo,
			hasMedicare: this.hasMedicare,
			hasMedicaid: this.hasMedicaid,

			errorMessage: this.errorMessage,

			ipAddress: this.ipAddress,
			userAgent: this.userAgent,
			referrerUrl: this.referrerUrl,

			importedAt: this.importedAt,

			insuranceCall: this.insuranceCall,
			originalSubmission: this.originalSubmission,
			originalLookupSubmission: this.originalLookupSubmission,

			sentWelcomeEmail: this.sentWelcomeEmail,
			sentEmails: this.sentEmails,

			notes: this.notes,
			notesCount: this.notesCount,
			documents: this.documents,
			documentsCount: this.documentsCount,
			agreements: this.agreements,
			agreementsCount: this.agreementsCount,
			forms: this.forms,
			formsCount: this.formsCount,

			coveredEntityId: this.coveredEntityId,
			pharmacyId: this.pharmacyId,

			phoneVerified: this.phoneVerified,

			testingReason: this.testingReason,

			acceptAgreements: this.acceptAgreements,
			certifySymptoms: this.certifySymptoms,
			acceptConsentAgreements: this.acceptConsentAgreements,

			orderIds: this.orderIds,
			subscriptionIds: this.subscriptionIds,

			driversLicenseFrontImageRefId: this.driversLicenseFrontImageRefId,
			driversLicenseBackImageRefId: this.driversLicenseBackImageRefId,

			selfImageRefId: this.selfImageRefId,

			requiresConfirmation: this.requiresConfirmation,
			isConfirmed: this.isConfirmed,
			confirmedAt: this.confirmedAt,

			requiresClinicianApproval: this.requiresClinicianApproval,
			isApproved: this.isApproved,
			approvedAt: this.approvedAt,

			requiresClinicianEncounter: this.requiresClinicianEncounter,
			encounterIds: this.encounterIds,
			encounterAt: this.encounterAt,

			requiresSurveys: this.requiresSurveys,
			isSurveysCompleted: this.isSurveysCompleted,
			surveysCompletedAt: this.surveysCompletedAt,

			requiredDocuments: this.requiredDocuments,
			isRequiredDocumentsProvided: this.isRequiredDocumentsProvided,
			requiredDocumentsProvidedAt: this.requiredDocumentsProvidedAt,

			lastFunnelStep: this.lastFunnelStep,
			cartItems: this.cartItems,

			shippingState: this.shippingState,

			repId: this.repId,

			couponCode: this.couponCode,
			referralUserId: this.referralUserId,
			referralAffiliateId: this.referralAffiliateId,
			referralSubAffiliateId: this.referralSubAffiliateId,

			testMode: this.testMode,

			addon: this.addon
		};
	}

	static uploadFrontPhoto = async (userId, coveredEntityId, leadId, frontPhoto) => {
		if (isNotNullOrUndefined(frontPhoto)) {
			const coveredEntityIds = [];
			coveredEntityIds.push(coveredEntityId);

			const fileId = uuid();
			const fileName = fileId + getFileExt(frontPhoto.name);
			const uploadRes = await addLeadFile(frontPhoto, 'documents', fileName, 'INSURANCE_CARD_FRONT', 'PENDING', userId, leadId, coveredEntityIds);
			// console.log(uploadRes);
			if (isObjectNotEmpty(uploadRes)) {
				return uploadRes.ref.fullPath;
			}
		}

		return null;
	}

	static uploadBackPhoto = async (userId, coveredEntityId, leadId, backPhoto) => {
		if (isNotNullOrUndefined(backPhoto)) {
			const coveredEntityIds = [];
			coveredEntityIds.push(coveredEntityId);

			const fileId = uuid();
			const fileName = fileId + getFileExt(backPhoto.name);
			const uploadRes = await addLeadFile(backPhoto, 'documents', fileName, 'INSURANCE_CARD_BACK', 'PENDING', userId, leadId, coveredEntityIds);
			// console.log(uploadRes);
			if (isObjectNotEmpty(uploadRes)) {
				return uploadRes.ref.fullPath;
			}
		}

		return null;
	}

	create = async () => {
		try {
			if (isStringEmpty(this.ipAddress)) {
				this.ipAddress = await getClientIPAddress();
			}
			if (isStringEmpty(this.userAgent)) {
				this.userAgent = getClientUserAgent();
			}
			if (isStringEmpty(this.referrerUrl)) {
				this.referrerUrl = getReferrerUrl();
			}
		} catch (error) {
			console.log(error);
		}

		return await createBlind(Lead.collectionName, this.toDto());
	}

	static updateField = async (id, fieldName, newValue) => {
		if (isStringNotEmpty(id)) {
			return await updateField(Lead.collectionName, id, fieldName, newValue);
		} else {
			throw new Error("id is required.");
		}
	}

	static updateFields = async (id, updatedFields) => {
		if (isStringNotEmpty(id)) {
			if (updatedFields['firstName'] !== undefined) {
				updatedFields['firstNameLower'] = toLower(updatedFields['firstName']);
			}
			if (updatedFields['lastName'] !== undefined) {
				updatedFields['lastNameLower'] = toLower(updatedFields['lastName']);
			}

			return await updateFields(Lead.collectionName, id, updatedFields);
		} else {
			throw new Error("id is required.");
		}
	}

	static update = async (id, updatedLead) => {
		const updatedObj = new Lead(updatedLead);
		if (id === updatedLead.id) {
			return await update(Lead.collectionName, id, updatedObj.toDto());
		} else {
			throw new Error('id and id field of the updated lead must match.')
		}
	}

	static delete = async (id) => {
		if (isStringNotEmpty(id)) {
			return await remove(Lead.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static updateRep = async (leadId, repId) => {
		return await updateField(Lead.collectionName, leadId, 'repId', repId);
	}

	static assignRepToLeads = async (repId, leadCount, state) => {
		let leads = await this.getPaginated({
			state: state,
			repId: "NONE"
		}, leadCount, null, null);

		for (const lead of leads) {
			await this.updateRep(lead.id, repId);
		}

		return true;
	}

	static transferRepToLeads = async (repId, leads) => {
		for (const lead of leads) {
			await this.updateRep(lead.id, repId);
		}

		return true;
	}

	static assignRepToLeadIds = async (repId, leadIds) => {
		for (const leadId of leadIds) {
			await this.updateRep(leadId, repId);
		}

		return true;
	}

	static getById = async (id) => {
		if (isStringNotEmpty(id)) {
			return await getById(Lead.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getAll = async () => {
		return await getAll(Lead.collectionName);
	}

	static getByField = async (fieldName, fieldValue, comparer = '==') => {
		return await getBySingleQuery(Lead.collectionName, fieldName, fieldValue, comparer);
	}

	static getByOrderId = async (orderId) => {
		return await getBySingleQuery(Lead.collectionName, 'orderIds', orderId, 'array-contains');
	}

	static getBySubscriptionId = async (subscriptionId) => {
		return await getBySingleQuery(Lead.collectionName, 'subscriptionIds', subscriptionId, 'array-contains');
	}

	static getByQueryConstraints = async (queryConstraints = []) => {
		let q = query(collection(db, Lead.collectionName), ...queryConstraints);

		const snapShot = await getDocs(q);
		// console.log(snapShot)
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id, // append document id to each document
				...doc.data()
			};
		});
	}

	static getByPhone = async (phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByEmail = async (email) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static search = async (searchParams) => {
		let { coveredEntityId, firstName, lastName, email, phone, subscriberId, userId, status, programId, productSku, leadId } = searchParams;

		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(userId)) {
			queryConstraints.push(where('userId', '==', userId));
		}

		if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
		}

		if (isStringNotEmptyOrZeroLength(programId)) {
			queryConstraints.push(where('programId', '==', programId));
		}

		if (isListNotEmpty(status)) {
			queryConstraints.push(where('status', 'in', status));
		} else if (isStringNotEmptyOrZeroLength(status)) {
			queryConstraints.push(where('status', '==', status));
		}

		if (isStringNotEmptyOrZeroLength(firstName)) {
			queryConstraints.push(where('firstNameLower', '==', toLower(firstName)));
		}

		if (isStringNotEmptyOrZeroLength(lastName)) {
			queryConstraints.push(where('lastNameLower', '==', toLower(lastName)));
		}

		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}

		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		if (isStringNotEmptyOrZeroLength(subscriberId)) {
			queryConstraints.push(where('subscriberId', '==', subscriberId));
		}

		if (isStringNotEmptyOrZeroLength(productSku)) {
			queryConstraints.push(where('productSku', '==', productSku));
		}

		if (isStringNotEmptyOrZeroLength(leadId)) {
			queryConstraints.push(where(documentId(), '==', leadId));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getReadyToImport = async () => {
		const collectionRef = collection(db, Lead.collectionName);
		let q = query(collectionRef, where('status', 'in', ['SUBMITTED']), where('payerCode', 'in', ["00138", "00428", "00429"]));

		const snapshot = await getDocs(q);
		return snapshot.docs.map((doc) => {
			return {
				id: doc.id, // append document id to each document
				...doc.data()
			};
		});
	}

	static getCount = async (queryParameter) => {
		const { testMode, userId, status, repId, affiliateId, hasMedicare, hasMedicaid, isConversion, state, planType, insuranceStatus, payerCode, coveredEntityId, programId, start, end } = queryParameter;

		let queryConstraints = [];

		if (isBooleanTrue(testMode)) {
			queryConstraints.push(where('testMode', '==', true));
		} else if (isBooleanFalse(testMode)) {
			queryConstraints.push(where('testMode', '==', false));
		}

		if (isListNotEmpty(userId)) {
			queryConstraints.push(where('userId', 'in', userId));
		} else if (isStringNotEmptyOrZeroLength(userId)) {
			queryConstraints.push(where('userId', '==', userId));
		}

		if (isListNotEmpty(affiliateId)) {
			queryConstraints.push(where('affiliateId', 'in', affiliateId));
		} else if (isStringNotEmptyOrZeroLength(affiliateId)) {
			queryConstraints.push(where('affiliateId', '==', affiliateId));
		}

		if (isListNotEmpty(status)) {
			queryConstraints.push(where('status', 'in', status));
		} else if (isStringNotEmptyOrZeroLength(status)) {
			queryConstraints.push(where('status', '==', status));
		}

		if (isBooleanTrue(hasMedicare)) {
			queryConstraints.push(where('hasMedicare', '==', true));
		} else if (isBooleanFalse(hasMedicare)) {
			queryConstraints.push(where('hasMedicare', '==', false));
		}

		if (isBooleanTrue(hasMedicaid)) {
			queryConstraints.push(where('hasMedicaid', '==', true));
		} else if (isBooleanFalse(hasMedicaid)) {
			queryConstraints.push(where('hasMedicaid', '==', false));
		}

		if (isBooleanTrue(isConversion)) {
			queryConstraints.push(where('isConversion', '==', true));
		} else if (isBooleanFalse(isConversion)) {
			queryConstraints.push(where('isConversion', '==', false));
		}

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		if (isListNotEmpty(planType)) {
			queryConstraints.push(where('planType', 'in', planType));
		} else if (isStringNotEmptyOrZeroLength(planType)) {
			queryConstraints.push(where('planType', '==', planType));
		}

		if (isListNotEmpty(insuranceStatus)) {
			queryConstraints.push(where('insuranceStatus', 'in', insuranceStatus));
		} else if (isStringNotEmptyOrZeroLength(insuranceStatus)) {
			queryConstraints.push(where('insuranceStatus', '==', insuranceStatus));
		}

		if (isListNotEmpty(payerCode)) {
			queryConstraints.push(where('payerCode', 'in', payerCode));
		} else if (isStringNotEmptyOrZeroLength(payerCode)) {
			queryConstraints.push(where('payerCode', '==', payerCode));
		}

		if (isListNotEmpty(programId)) {
			queryConstraints.push(where('programId', 'in', programId));
		} else if (isStringNotEmptyOrZeroLength(programId)) {
			queryConstraints.push(where('programId', '==', programId));
		}

		if (isListNotEmpty(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			if (coveredEntityId === 'NONE') {
				queryConstraints.push(where('coveredEntityId', '==', null));
			} else {
				queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
			}
		}

		if (isListNotEmpty(repId)) {
			queryConstraints.push(where('repId', 'in', repId));
		} else if (isStringNotEmptyOrZeroLength(repId)) {
			if (repId === 'NONE') {
				queryConstraints.push(where('repId', '==', 'NONE'));
			} else if (repId === 'ASSIGNED') {
				queryConstraints.push(where('repId', '!=', 'NONE'));
			} else {
				queryConstraints.push(where('repId', '==', repId));
			}
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = query(collection(db, Lead.collectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);
		// console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameter, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
		const { testMode, userId, status, repId, affiliateId, hasMedicare, hasMedicaid, isConversion, state, planType, insuranceStatus, payerCode, coveredEntityId, programId, start, end } = queryParameter;

		let queryConstraints = [];

		if (isBooleanTrue(testMode)) {
			queryConstraints.push(where('testMode', '==', true));
		} else if (isBooleanFalse(testMode)) {
			queryConstraints.push(where('testMode', '==', false));
		}

		if (isListNotEmpty(userId)) {
			queryConstraints.push(where('userId', 'in', userId));
		} else if (isStringNotEmptyOrZeroLength(userId)) {
			queryConstraints.push(where('userId', '==', userId));
		}

		if (isListNotEmpty(affiliateId)) {
			queryConstraints.push(where('affiliateId', 'in', affiliateId));
		} else if (isStringNotEmptyOrZeroLength(affiliateId)) {
			queryConstraints.push(where('affiliateId', '==', affiliateId));
		}

		if (isListNotEmpty(status)) {
			queryConstraints.push(where('status', 'in', status));
		} else if (isStringNotEmptyOrZeroLength(status)) {
			queryConstraints.push(where('status', '==', status));
		}

		if (isBooleanTrue(hasMedicare)) {
			queryConstraints.push(where('hasMedicare', '==', true));
		} else if (isBooleanFalse(hasMedicare)) {
			queryConstraints.push(where('hasMedicare', '==', false));
		}

		if (isBooleanTrue(hasMedicaid)) {
			queryConstraints.push(where('hasMedicaid', '==', true));
		} else if (isBooleanFalse(hasMedicaid)) {
			queryConstraints.push(where('hasMedicaid', '==', false));
		}

		if (isBooleanTrue(isConversion)) {
			queryConstraints.push(where('isConversion', '==', true));
		} else if (isBooleanFalse(isConversion)) {
			queryConstraints.push(where('isConversion', '==', false));
		}

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		if (isListNotEmpty(planType)) {
			queryConstraints.push(where('planType', 'in', planType));
		} else if (isStringNotEmptyOrZeroLength(planType)) {
			queryConstraints.push(where('planType', '==', planType));
		}

		if (isListNotEmpty(insuranceStatus)) {
			queryConstraints.push(where('insuranceStatus', 'in', insuranceStatus));
		} else if (isStringNotEmptyOrZeroLength(insuranceStatus)) {
			queryConstraints.push(where('insuranceStatus', '==', insuranceStatus));
		}

		if (isListNotEmpty(payerCode)) {
			queryConstraints.push(where('payerCode', 'in', payerCode));
		} else if (isStringNotEmptyOrZeroLength(payerCode)) {
			queryConstraints.push(where('payerCode', '==', payerCode));
		}

		if (isListNotEmpty(programId)) {
			queryConstraints.push(where('programId', 'in', programId));
		} else if (isStringNotEmptyOrZeroLength(programId)) {
			queryConstraints.push(where('programId', '==', programId));
		}

		if (isListNotEmpty(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			if (coveredEntityId === 'NONE') {
				queryConstraints.push(where('coveredEntityId', '==', null));
			} else {
				queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
			}
		}

		if (isListNotEmpty(repId)) {
			queryConstraints.push(where('repId', 'in', repId));
		} else if (isStringNotEmptyOrZeroLength(repId)) {
			if (repId === 'NONE') {
				queryConstraints.push(where('repId', '==', 'NONE'));
			} else if (repId === 'ASSIGNED') {
				queryConstraints.push(where('repId', '!=', 'NONE'));
			} else {
				queryConstraints.push(where('repId', '==', repId));
			}
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = null;
		if (isStringNotEmpty(firstVisible)) {
			q = query(collection(db, Lead.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['repId'], firstVisible['id']));
		} else if (isStringNotEmpty(lastVisible)) {
			q = query(collection(db, Lead.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['repId'], lastVisible['id']));
		} else {
			q = query(collection(db, Lead.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
		}

		const snapShot = await getDocs(q);
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id, // append document id to each document
				...doc.data()
			};
		});
	}

	static get = async (queryParameter) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await Lead.getCount(queryParameter);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await Lead.getPaginated(queryParameter, 10000, null, next, 'createdAt');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}

	static existsBySubscriberId = async (leadId, subscriberId, dateOfBirth, firstName) => {
		let exists = false;
		// console.log(leadId);
		// console.log(subscriberId);
		if (isStringNotEmpty(subscriberId) && isStringNotEmpty(dateOfBirth) && isStringNotEmpty(firstName) && isStringNotEmpty(leadId)) {
			const func = httpsCallable(functions, "doesleadexistbysubscriberid", { timeout: 6000000 });
			const res = await func({ subscriberId: subscriberId, dateOfBirth: dateOfBirth, firstName: firstName });
			// console.log(res.data);
			if (isObjectNotEmpty(res.data)) {
				if (isBooleanTrue(res.data.exists)) {
					if (isListNotEmpty(res.data.ids)) {
						let ids = res.data.ids;

						if (ids.includes(leadId)) {
							if (ids.length > 1) {
								exists = true;
							}
						} else {
							exists = true;
						}
					} else {
						exists = true;
					}
				}
			}
		}

		return exists;
	}

	static existsByEmail = async (email) => {
		let exists = false;
		// console.log(email);
		if (isStringNotEmpty(email)) {
			const func = httpsCallable(functions, "doesleadexistbyemail", { timeout: 6000000 });
			const res = await func({ email: email });
			// console.log(res.data);
			return isObjectNotEmpty(res.data) && isBooleanTrue(res.data.exists);
		}

		return exists;
	}

	static updateUserFromLead = async (lead, userId) => {
		if (isObjectNotEmpty(lead) && isStringNotEmpty(userId)) {
			let existingUser = await User.getById(userId);
			if (isObjectNotEmpty(existingUser)) {
				let userRefId = isStringNotEmpty(lead.userRefId) ? lead.userRefId : null;

				let email = toLower(lead.email);
				let phone = isStringNotEmpty(lead.phone) ? toE164PhoneNumber(lead.phone) : null;

				let firstName = lead.firstName;
				let lastName = lead.lastName;
				let middleName = isStringNotEmpty(lead.middleName) ? lead.middleName : null;
				let suffix = isStringNotEmpty(lead.suffix) ? lead.suffix : null;

				let gender = isStringNotEmpty(lead.gender) ? lead.gender : null;
				let dateOfBirth = isStringNotEmpty(lead.dateOfBirth) ? lead.dateOfBirth : null;

				let socialSecurityNumber = isStringNotEmpty(lead.socialSecurityNumber) ? lead.socialSecurityNumber : null;

				let shippingAddress = isObjectNotEmpty(lead.shippingAddress) ? lead.shippingAddress : null;
				let billingAddress = isObjectNotEmpty(lead.billingAddress) ? lead.billingAddress : null;

				try {
					await AuthUser.update(userId, email !== existingUser.email ? email : undefined, undefined, firstName, lastName);
				} catch (error) {
					console.log(error);
				}

				try {
					let updatedUserFields = {
						userRefId: userRefId,

						email: email,
						phone: phone,

						firstName: firstName,
						firstNameLower: toLower(firstName),
						middleName: middleName,
						lastName: lastName,
						lastNameLower: toLower(lastName),
						suffix: suffix,

						gender: gender,
						dateOfBirth: dateOfBirth,

						socialSecurityNumber: socialSecurityNumber,

						billingAddress: billingAddress,
						shippingAddress: shippingAddress
					};

					await User.updateFields(userId, updatedUserFields);
				} catch (error) {
					console.log(error.message);
				}

				try {
					return await Lead.updateFields(lead.id, { status: 'IMPORTED', importedAt: moment.tz('US/Pacific').utc().toDate() });
				} catch (error) {
					console.log(error.message);
				}
			}
		}

		return false;
	}

	static mergeLeadWithExistingUser = async (lead, userId, insurances, programId, userRefId, coveredEntityId) => {
		if (isObjectNotEmpty(lead) && isStringNotEmpty(userId) && isStringNotEmpty(programId) && isStringNotEmpty(coveredEntityId)) {
			let existingUser = await User.getById(userId);
			if (isObjectNotEmpty(existingUser)) {
				let email = toLower(lead.email);
				let phone = isStringNotEmpty(lead.phone) ? toE164PhoneNumber(lead.phone) : null;

				let firstName = lead.firstName;
				let lastName = lead.lastName;
				let middleName = isStringNotEmpty(lead.middleName) ? lead.middleName : null;
				let suffix = isStringNotEmpty(lead.suffix) ? lead.suffix : null;

				let gender = isStringNotEmpty(lead.gender) ? lead.gender : null;
				let dateOfBirth = isStringNotEmpty(lead.dateOfBirth) ? lead.dateOfBirth : null;

				let socialSecurityNumber = isStringNotEmpty(lead.socialSecurityNumber) ? lead.socialSecurityNumber : null;

				let shippingAddress = isObjectNotEmpty(lead.shippingAddress) ? lead.shippingAddress : null;
				let billingAddress = isObjectNotEmpty(lead.billingAddress) ? lead.billingAddress : null;

				try {
					await AuthUser.update(userId, email !== existingUser.email ? email : undefined, undefined, firstName, lastName);
				} catch (error) {
					console.log(error);
				}

				try {
					let updatedUserFields = {
						userRefId: userRefId,

						email: email,
						phone: phone,

						firstName: firstName,
						middleName: middleName,
						lastName: lastName,
						suffix: suffix,

						gender: gender,
						dateOfBirth: dateOfBirth,

						socialSecurityNumber: socialSecurityNumber,

						billingAddress: billingAddress,
						shippingAddress: shippingAddress
					};

					await User.updateFields(userId, updatedUserFields);
				} catch (error) {
					console.log(error.message);
				}

				try {
					await UserNote.create(userId, {
						body: `Opted in through the lead form ${lead.source} and subAffiliateId ${lead.subAffiliateId} and affiliateId ${lead.affiliateId} on ${fromUtcToTimeZone(lead.createdAt, 'US/Pacific')}`,
						by: userId,
						at: lead.createdAt
					});
				} catch (error) {
					console.log(error);
				}

				try {
					// create first order and subscription if subscription for this programId doesnt already exist
					let existingSubscription = await Subscription.getByUserIdAndProgramIdAndStatus(userId, programId, 'ACTIVE');
					if (isObjectNotEmpty(existingSubscription)) {
						// exists already so no need to create a duplicate subscription
						return await Lead.updateFields(lead.id, { status: 'IMPORTED', importedAt: moment.tz('US/Pacific').utc().toDate() });
					} else {
						const subscriptionSuccess = await Subscription.startSubscription(lead, userId, insurances, programId, coveredEntityId);
						if (isBooleanTrue(subscriptionSuccess)) {
							return await Lead.updateFields(lead.id, { status: 'IMPORTED', importedAt: moment.tz('US/Pacific').utc().toDate() });
						}
					}
				} catch (error) {
					console.log(error.message);
				}
			}
		}

		return false;
	}

	static importLeadAsNewUser = async (lead, insurances = [], programId = null, userRefId, coveredEntityId) => {
		if (isObjectNotEmpty(lead) && isStringNotEmpty(programId) && isStringNotEmpty(coveredEntityId)) {
			let userId = null;
			let email = toLower(lead.email);
			let phone = toE164PhoneNumber(lead.phone);

			let firstName = lead.firstName;
			let lastName = lead.lastName;
			let middleName = isStringNotEmpty(lead.middleName) ? lead.middleName : null;
			let suffix = isStringNotEmpty(lead.suffix) ? lead.suffix : null;

			let gender = isStringNotEmpty(lead.gender) ? lead.gender : null;
			let dateOfBirth = isStringNotEmpty(lead.dateOfBirth) ? lead.dateOfBirth : null;

			let socialSecurityNumber = isStringNotEmpty(lead.socialSecurityNumber) ? lead.socialSecurityNumber : null;

			let shippingAddress = isObjectNotEmpty(lead.shippingAddress) ? lead.shippingAddress : null;
			let billingAddress = isObjectNotEmpty(lead.billingAddress) ? lead.billingAddress : (isObjectNotEmpty(lead.shippingAddress) ? lead.shippingAddress : null);

			try {
				userId = await AuthUser.create(email, undefined, firstName, lastName, false, false);
				if (isStringNotEmpty(userId)) {
					try {
						let newUserFields = {
							userId: userId,
							patientId: userRefId,
							userRefId: userRefId,

							email: email,
							phone: phone,

							firstName: firstName,
							middleName: middleName,
							lastName: lastName,
							suffix: suffix,

							gender: gender,
							dateOfBirth: dateOfBirth,

							socialSecurityNumber: socialSecurityNumber,

							billingAddress: billingAddress,
							shippingAddress: shippingAddress
						};

						if (isBooleanTrue(lead.phoneVerified)) {
							newUserFields.phoneVerified = true;
						}

						let newUser = await new User(newUserFields).create();
						// console.log(newUser);

						try {
							await UserNote.create(userId, {
								body: `Opted in through the lead form ${lead.source} and subAffiliateId ${lead.subAffiliateId} and affiliateId ${lead.affiliateId} on ${fromUtcToTimeZone(lead.createdAt, 'US/Pacific')}`,
								by: userId,
								at: lead.createdAt
							});
						} catch (error) {
							console.log(error);
						}

						try {
							for (const insurance of insurances) {
								await new UserInsurance({
									userId: userId,
									subscriberId: insurance.subscriberId,
									rank: insurance.rank,
									hierarchy: insurance.hierarchy,
									status: insurance.status,
									isMedicare: insurance.isMedicare,
									isMedicaid: insurance.isMedicaid,

									firstName: insurance.firstName,
									middleName: insurance.middleName,
									lastName: insurance.lastName,
									suffix: insurance.suffix,

									gender: insurance.gender,
									dateOfBirth: insurance.dateOfBirth,

									socialSecurityNumber: insurance.socialSecurityNumber,

									patientPhone: insurance.patientPhone,
									patientAddress: insurance.patientAddress,
									relationshipToSubscriber: insurance.relationshipToSubscriber,

									insuranceAddress: insurance.insuranceAddress,
									insurancePhone: insurance.insurancePhone,

									subscriberFirstName: insurance.subscriberFirstName,
									subscriberLastName: insurance.subscriberLastName,
									subscriberPhone: insurance.subscriberPhone,
									subscriberAddress: insurance.subscriberAddress,

									payerName: insurance.payerName,
									payerCode: insurance.payerCode,
									planName: insurance.planName,
									planNumber: insurance.planNumber,
									groupName: insurance.groupName,
									groupNumber: insurance.groupNumber,
									planType: insurance.planType,
									policyType: insurance.policyType,

									rxBin: insurance.rxBin,
									rxPcn: insurance.rxPcn,
									rxGroup: insurance.rxGroup,

									effectiveFromDate: insurance.effectiveFromDate,
									effectiveToDate: insurance.effectiveToDate,

									insuranceInfo: insurance.insuranceInfo
								}).create();
							}
						} catch (error) {
							console.log(error.message);
						}

						// create first order and subscription
						const subscriptionSuccess = await Subscription.startSubscription(lead, userId, insurances, programId, coveredEntityId);
						if (isBooleanTrue(subscriptionSuccess)) {
							return await Lead.updateFields(lead.id, { status: 'IMPORTED', userId: userId, importedAt: moment.tz('US/Pacific').utc().toDate() });
						}
					} catch (error) {
						console.log(error.message);
						throw new Error(error.message);
					}
				}
			} catch (error) {
				console.log(error);
				throw new Error(error.message);
			}
		}

		return false;
	}

	static updateLeads = async () => {
		const list = await this.get({});
		console.log('Total Leads', list.length);

		let chunks = [];
		for (let i = 0; i < list.length; i += 500) {
			chunks.push(list.slice(i, i + 500))
		}

		console.log(chunks.length);

		for (var chunk of chunks) {
			// Get a new write batch
			var batch = writeBatch(db);
			chunk.map((item) => {
				const leadRef = doc(collection(db, Lead.collectionName), item.id);

				let updatedFields = {};

				if (isListNotEmpty(item.cartItems)) {
					updatedFields.cartItems = item.cartItems.map((cartItem) => {
						if (isObjectNotEmpty(cartItem?.product) && isStringEmpty(cartItem?.product?.sku)) {
							cartItem.sku = toUpper(cartItem?.product?.sku);
							delete cartItem.product;
						}
						if (isStringNotEmpty(cartItem?.variantId) && isObjectNotEmpty(cartItem?.variant) && isStringEmpty(cartItem?.variant?.sku)) {
							cartItem.variantSku = toUpper(cartItem?.variant?.sku);
							delete cartItem.variant;
						}

						return cartItem;
					});

					batch.update(leadRef, updatedFields);
				}
			});
			await batch.commit();
			console.log('done with chunk', chunks.indexOf(chunk) + 1);
		}
	}

	static updateLeads2 = async () => {
		let products = await Product.getAll();
		let variants = await ProductVariant.getAll();

		let nextLeadPage = null;
		let successCount = 0;
		let skipCount = 0;
		let totalLeadPages = 0;
		let totalLeadCount = await Lead.getCount({});
		totalLeadPages = Math.ceil(totalLeadCount / 500.0);
		console.log(totalLeadCount);
		console.log(totalLeadPages);
		let leadPages = [];
		for (let i = 0; i < totalLeadPages; i++) {
			leadPages.push(i + 1);
		}

		for (const page of leadPages) {
			console.log('starting page ' + page);
			const list = await Lead.getPaginated({}, 500, null, nextLeadPage, 'createdAt');
			console.log('fetched leads: ' + list.length);

			var batch = writeBatch(db);
			list.map((item) => {
				const leadRef = doc(collection(db, Lead.collectionName), item.id);

				let updatedFields = {};

				if (isListNotEmpty(item.cartItems) && isBooleanTrue(item?.cartItems?.some(i => isObjectNotEmpty(i?.product) || isObjectNotEmpty(i?.variant)))) {
					// console.log(item.cartItems);
					updatedFields.cartItems = item.cartItems.map((cartItem) => {
						let sku = null;
						let variantSku = null;

						if (isObjectNotEmpty(cartItem?.product) && isStringNotEmpty(cartItem?.product?.sku)) {
							sku = toUpper(cartItem?.product?.sku);
						} else if (isStringNotEmpty(cartItem?.id)) {
							sku = getProductSkuById(cartItem?.id, products);
						}

						if (isObjectNotEmpty(cartItem?.variant) && isStringNotEmpty(cartItem?.variant?.sku)) {
							variantSku = toUpper(cartItem?.variant?.sku);
						} else if (isStringNotEmpty(cartItem?.variantId)) {
							variantSku = getVariantSkuById(cartItem?.variantId, variants);
						}

						return new CartItem({
							...cartItem,
							sku: sku,
							variantSku: variantSku
						}).toDto();
					});

					batch.update(leadRef, updatedFields);
					successCount++;
				} else {
					skipCount++;
				}
			});

			await batch.commit();

			console.log('go to next page', successCount, skipCount);
			nextLeadPage = list[list.length - 1];
		}
	}

	static generateLeadBillingReport = async () => {
		const func = httpsCallable(functions, "generateleadbillingreport", { timeout: 6000000 });

		const res = await func();
		// console.log(res.data);

		let urls = res.data;

		return urls;
	}

	static getLeadAggregates = async (start, end) => {
		const func = httpsCallable(functions, "getleadaggregates", { timeout: 6000000 });
		const res = await func({
			start,
			end
		});

		return res.data;
	}

	static getLeadAffiliateAggregates = async (affiliateId, start, end) => {
		const func = httpsCallable(functions, "getleadaffiliateaggregates", { timeout: 6000000 });
		const res = await func({
			affiliateId: affiliateId,
			start,
			end
		});

		return res.data;
	}
}

export default Lead;
